<template>
    <div class="sku-bulk-upload-container">
        <div class="sku-bulk-upload-drop-zone">
            <vue-dropzone
                v-show="!processing.visible"
                :id="`vue-dropzone-${parentId}`"
                :ref="`dropzone-${parentId}`"
                :options="dropzoneOptions"
                :use-custom-slot="true"
                :include-styling="false"
                class="vue-dropzone"
                @vdropzone-file-added="fileUploadAdded()"
                @vdropzone-sending="(file, xhr) => fileUploadSending(file, xhr)"
                @vdropzone-upload-progress="(file, progress) => fileUploadProgress(file, progress)"
                @vdropzone-success="(file, items) => fileUploaded(file, items)"
                @vdropzone-complete="fileUploadComplete()"
                @vdropzone-max-files-exceeded="() => fileUploadError($t('items.bulk.error.multipleFiles'))"
                @vdropzone-error="(file, message) =>
                    fileUploadError(message || $t('items.bulk.error.serverError', {file:file.name}))"
            >
                <button type="button"
                        class="drop-zone"
                >
                    <svg class="drop-zone__icon"
                         width="90"
                         height="90"
                    >
                        <use xlink:href="#file-upload" />
                    </svg>
                    <p class="drop-zone__title">
                        {{ $t('items.bulk.dropSkuTemplate') }}
                    </p>
                    <div class="drop-zone__text">
                        {{ $t('general.or') }}
                        <span v-t="'general.files.selectFile'"
                              class="drop-zone__link"
                        />
                    </div>
                </button>
            </vue-dropzone>

            <progress-bar v-if="processing.visible"
                          slot="overlay"
                          :allow-cancel="stage !== 'cancelled'"
                          :cancel-text="$t('general.cancel')"
                          :value="processing.value"
                          :message="processing.message"
                          @cancel="onCancelAction"
            />
        </div>
        <div class="need-template">
            <label class="need-template-label">
                {{ $t('items.bulk.needSkuTemplate') }}
            </label>
            <base-button tag="a"
                         :href="downloadTemplateUrl"
                         target="_blank"
                         primary
                         size="sm"
            >
                <svg slot="iconLeft"
                     width="16"
                     height="16"
                >
                    <use xlink:href="#download" />
                </svg>
                {{ $t('general.files.download') }}
            </base-button>
            <div class="sku-limit-info">
                {{
                    parentComponent == 'asn'
                        ? $t('items.bulk.skuLimitInfoAsn', { skuLimit: maxSkuInFile })
                        : $t('items.bulk.skuLimitInfo', { skuLimit: maxSkuInFile })
                }}
            </div>
        </div>
    </div>
</template>

<script>
import '@/assets/icons/download.svg';
import VueDropzone from 'vue2-dropzone';
import ProgressBar from '@/components/ProgressBar';
import { BaseButton } from '@/components/buttons';
import { $t } from '@/translations';
import constants from '@/core/constants';

const Stages = {
    none: 'none',
    uploading: 'uploading',
    processing: 'processing',
    editing: 'editing',
    saving: 'saving',
    cancelled: 'cancelled',
};

export default {
    name: 'SkuBulkUpload',
    components: {
        BaseButton,
        VueDropzone,
        ProgressBar,
    },
    props: {
        id: {
            type: String,
            required: true,
        },
        itemsParentId: {
            type: Number,
            required: false,
            default: 1,
        },
        parentComponent: {
            type: String,
            default: null,
        },
        skuOnly: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            downloadTemplateUrl: this.skuOnly ? `${constants.assetsUrl}/TRADING PARTNER INVENTORY ASSIGNMENT_TEMPLATE.csv` : `${constants.assetsUrl}/import_skus_template.csv`,
            incrementalId: this.itemsParentId + new Date().valueOf(), // is used to generate consistent line item id
            dropzoneOptions: {
                url: this.skuOnly ? `${process.env.VUE_APP_API_URL}/sku/upload` : `${process.env.VUE_APP_API_URL}/skuWithQuantity/upload`,
                maxFiles: 1,
                autoProcessQueue: true,
                withCredentials: true,
                acceptedFiles: '.csv,.xlsx,.xls',
                previewTemplate: '<div></div>',
            },
            stage: Stages.none,
            processing: {
                visible: false,
                value: 0,
                message: '',
                orderId: null,
                start(message, value = 0, orderId) {
                    this.visible = true;
                    this.message = message;
                    this.value = value;
                    this.orderId = orderId;
                },
                progress(value) {
                    this.value = value;
                },
                done() {
                    this.visible = false;
                    this.orderId = null;
                },
            },
        };
    },
    computed: {
        parentId() {
            return this.itemsParentId;
        },
        maxSkuInFile() {
            return constants.bulkUpload.maxSkusInFile;
        },
    },
    methods: {
        genId() {
            this.incrementalId += 1;
            return this.incrementalId;
        },
        async fileUploadAdded() {
            this.reset();
            this.processing.start($t('items.bulk.progressUploading'), 1, this.itemsParentId);
            this.stage = Stages.uploading;
        },
        fileUploadSending(file, xhr) {
            let download = 70;

            xhr.addEventListener('progress', () => {
                if (download < 90) {
                    download += 5;
                }
                this.processing.start($t('items.bulk.progressProcessing'), download, this.itemsParentId);
            });
        },
        fileUploadProgress(file, progress) {
            if (progress !== 100) {
                this.processing.progress(Math.round(progress * 0.6));
            } else {
                this.stage = Stages.processing;
                this.processing.start($t('items.bulk.progressProcessing'), 70, this.itemsParentId);
            }
            this.$forceUpdate();
        },
        async fileUploaded(file, items) {
            const mappedItems = items.map(item => ({
                ...item,
                idx: this.genId(),
            }));

            this.$emit('file-uploaded', mappedItems);
        },
        fileUploadComplete() {
            this.stage = Stages.editing;
            this.processing.done();

            this.$refs[`dropzone-${this.itemsParentId}`].removeAllFiles();
        },
        fileUploadError(err) {
            this.$toast.error(err);
            this.stage = Stages.none;
        },
        onCancelAction() {
            switch (this.stage) {
            case Stages.uploading:
            case Stages.processing:
                this.$refs[`dropzone-${this.processing.orderId}`].removeAllFiles(true);
                this.reset();
                break;
            case Stages.saving:
                this.stage = Stages.cancelled;
                this.processing.message = $t('items.bulk.stopping');
                break;
            default:
                break;
            }
        },
        reset() {
            this.invalidCount = 0;
            this.validity = [];
            this.stage = Stages.none;
            this.processing.done();
        },
    },
};
</script>

<style scoped lang="scss">
.drop-zone {
    $_this: &;

    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 194px;
    margin: 1em 0 1em auto;
    padding: 20px;
    border: 2px dashed $color-select-options-border;
    border-radius: $input-border-radius;
    background: none;
    text-align: center;

    &__icon {
        transition: $transition-quick;
        color: $color-separator;
    }

    &__title {
        margin: 0 0 .25em;
        font-size: 15px;
        font-weight: 500;
        line-height: 28px;
    }

    &__text {
        color: $color-separator-dark;
        font-family: $font-family-primary;
        font-size: $font-size-base;
        font-weight: 400;
    }

    &__link {
        color: $color-link;
        text-decoration: underline;
    }

    &:hover,
    &:focus {
        background: $color-background-light-4;

        #{$_this}__icon {
            color: $color-link;
        }
    }

    &:focus {
        @include input-box-focus();
    }

    &:hover {
        border-color: $color-button;
    }
}

.vue-dropzone {
    &.dz-drag-hover {
        .drop-zone {
            border-color: $color-button;
            background: $color-background-light-4;
        }

        .drop-zone__icon {
            color: $color-link;
        }
    }
}

.sku-bulk-upload-container {
    height: 214px;
    overflow: hidden;

    .sku-bulk-upload-drop-zone {
        width: 40%;
        height: 214px;
        float: right;
        overflow: hidden;

        &.drop-zone-no-template {
            margin: 0 auto;
            float: none;
        }
    }

    .need-template {
        margin-top: 60px;
        margin-left: 40px;

        &-label {
            margin-right: 5px;
            font-size: $font-size-base;
            font-weight: 500;
            line-height: 27px;
        }

        .sku-limit-info {
            width: 50%;
            padding-top: 20px;
            font-size: $font-size-base;
            line-height: 18px;
        }
    }
}

.progress-bar {
    padding-top: 20px;
}
</style>
